export const LOGIN_DESTINATIONS = {
  onboarding: '/onboarding',
  sharePhone: '/onboarding/share-phone',
  setPasscode: '/onboarding/passcode',
  unlock: '/unlock',
  forgotPin: '/forgot-pin',
  home: '/home',
};

const TELEGRAM_WRITE_ACCESS_TIMEOUT_MS = 60000;

export function isForgotPasswordLoginUser(user) {
  return Boolean(user?.token && user?.reg_data?.new_password);
}

export function getPostLoginPath(user) {
  const stages = user?.reg_data?.stages ?? {};

  if (stages.is_system_allowed === false) {
    return LOGIN_DESTINATIONS.forgotPin;
  }

  if (isForgotPasswordLoginUser(user)) {
    return LOGIN_DESTINATIONS.unlock;
  }

  const signedLegalDocuments = user?.signed_legal_documents;

  if (!Array.isArray(signedLegalDocuments) || signedLegalDocuments.length === 0) {
    return LOGIN_DESTINATIONS.onboarding;
  }

  if (stages.is_phone_verified === false) {
    return LOGIN_DESTINATIONS.sharePhone;
  }

  if (stages.is_phone_verified === true && stages.is_password_set === false) {
    return LOGIN_DESTINATIONS.setPasscode;
  }

  return LOGIN_DESTINATIONS.home;
}

export function getApiDetail(error) {
  return error?.payload?.detail ?? error?.detail ?? error?.message ?? null;
}

export function isMissingReferralError(error) {
  return error?.statusCode === 401 && getApiDetail(error) === 'Missing referral id';
}

export function isMissingWriteAccessError(error) {
  return error?.statusCode === 400 && getApiDetail(error) === 'Missing write access';
}

function parseInitDataUser(initData) {
  if (!initData) {
    return null;
  }

  try {
    const rawUser = new URLSearchParams(initData).get('user');
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
}

function getInitDataValue(initData, key) {
  if (!initData) {
    return undefined;
  }

  try {
    return new URLSearchParams(initData).get(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function getLaunchValue(key) {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const sources = [
    window.location.search,
    window.location.hash,
    window.location.href,
  ].filter(Boolean);

  for (const source of sources) {
    try {
      const normalizedSource = String(source).replace(/^[?#]/, '');
      const variants = [
        normalizedSource,
        normalizedSource.includes('?')
          ? normalizedSource.slice(normalizedSource.indexOf('?') + 1)
          : '',
      ].filter(Boolean);

      for (const variant of variants) {
        const value = new URLSearchParams(variant).get(key);

        if (value) {
          return value;
        }
      }
    } catch {
      // Ignore malformed launch params.
    }
  }

  return undefined;
}

function normalizeReferralId(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();

  return normalizedValue || undefined;
}

export function getTelegramReferralId({ webApp, initData } = {}) {
  const candidates = [
    webApp?.initDataUnsafe?.start_param,
    getInitDataValue(initData, 'start_param'),
    getLaunchValue('tgWebAppStartParam'),
  ];

  return candidates.map(normalizeReferralId).find(Boolean);
}

function normalizeTelegramBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    if (value === 0) return false;
    if (value === 1) return true;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();

    if (['false', '0', 'no', 'denied', 'disallowed'].includes(normalizedValue)) {
      return false;
    }

    if (['true', '1', 'yes', 'allowed'].includes(normalizedValue)) {
      return true;
    }
  }

  return null;
}

export function getTelegramAllowsWriteToPm({ webApp, initData } = {}) {
  const parsedUser = parseInitDataUser(initData);
  const candidates = [
    webApp?.initDataUnsafe?.user?.allows_write_to_pm,
    webApp?.initDataUnsafe?.allows_write_to_pm,
    getInitDataValue(initData, 'allows_write_to_pm'),
    parsedUser?.allows_write_to_pm,
  ].map(normalizeTelegramBoolean);

  if (candidates.includes(false)) {
    return false;
  }

  const value = candidates.find((candidate) => candidate !== null);

  return value === true;
}

function normalizeWriteAccessResult(value) {
  if (typeof value === 'object' && value !== null) {
    return normalizeWriteAccessResult(value.status);
  }

  return value === true || value === 'allowed';
}

export function requestTelegramWriteAccess(webApp) {
  return new Promise((resolve) => {
    if (!webApp?.requestWriteAccess) {
      resolve(false);
      return;
    }

    let didResolve = false;
    let timeoutId;
    let handleWriteAccessRequested;
    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (webApp.offEvent) {
        webApp.offEvent('writeAccessRequested', handleWriteAccessRequested);
      }
    };
    const finish = (value) => {
      if (!didResolve) {
        didResolve = true;
        cleanup();
        resolve(normalizeWriteAccessResult(value));
      }
    };
    handleWriteAccessRequested = (eventData) => {
      finish(eventData);
    };

    if (webApp.onEvent) {
      webApp.onEvent('writeAccessRequested', handleWriteAccessRequested);
    }

    if (typeof window !== 'undefined') {
      timeoutId = window.setTimeout(() => finish(false), TELEGRAM_WRITE_ACCESS_TIMEOUT_MS);
    }

    const result = webApp.requestWriteAccess(finish);
    if (result && typeof result.then === 'function') {
      result.then(finish).catch(() => finish(false));
    }
  });
}

export async function runTelegramLoginFlow({
  authService,
  deviceId,
  initData,
  referralId,
  theme,
  webApp,
}) {
  const nextReferralId = normalizeReferralId(referralId)
    || getTelegramReferralId({ webApp, initData });
  const currentUser = await authService.login({
    deviceId,
    initData,
    referralId: nextReferralId,
    theme,
  });

  return {
    currentUser,
    path: getPostLoginPath(currentUser),
  };
}
